import { ExternalLink, Newspaper } from 'lucide-react';

import { isSafeUrl } from '@/lib/utils';

export interface AdverseMediaEvidenceMetadata {
  articleTitle?: string;
  articleSnippet?: string;
  articleDate?: string;
  articleDomain?: string;
  confidence?: number;
  inferredSeverity?: string;
  pdfPath?: string;
  filename?: string;
}

export interface AdverseMediaEvidenceItem {
  filename?: string;
  url?: string;
  evidenceId?: string;
  source?: string;
  content?: string;
  metadata?: AdverseMediaEvidenceMetadata;
}

interface AdverseMediaSectionProps {
  evidence?: AdverseMediaEvidenceItem[];
  urls?: string[];
}

export function AdverseMediaSection({ evidence, urls }: AdverseMediaSectionProps) {
  const hasRichEvidence = evidence && evidence.length > 0;
  const hasPlainUrls = urls && urls.length > 0;

  if (!hasRichEvidence && !hasPlainUrls) return null;

  // Show rich article metadata when available
  if (hasRichEvidence) {
    return (
      <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-[#051923]">Adverse Media Findings</h2>
        <div className="space-y-2">
          {evidence.map((item, index) => {
            const title = item.metadata?.articleTitle;
            const date = item.metadata?.articleDate;
            const domain = item.metadata?.articleDomain;
            const url = item.url;

            // Skip items without a safe URL (prevents XSS via javascript:, data: URLs)
            if (!isSafeUrl(url)) return null;

            return (
              <div
                key={item.evidenceId || `evidence-${index}`}
                className="flex items-start justify-between gap-4 py-2"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <Newspaper className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <span className="block break-words text-sm">{title || domain || url}</span>
                    {(domain || date) && (
                      <span className="block text-xs text-gray-500">
                        {domain && date ? `${domain} • ${date}` : domain || date}
                      </span>
                    )}
                  </div>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex shrink-0 items-center gap-1 p-2 text-sm text-blue-600 hover:underline"
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // Fallback: plain URLs when no rich evidence metadata is available
  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-[#051923]">Sources</h2>
      <ul className="space-y-2">
        {urls!
          .filter((url) => isSafeUrl(url))
          .map((url) => (
            <li key={url} className="min-w-0">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-2 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="mt-0.5 h-5 w-5 shrink-0" />
                <span className="break-all">{url}</span>
              </a>
            </li>
          ))}
      </ul>
    </section>
  );
}
